import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ClipboardList, TrendingUp, Bike, CheckCircle2, Calendar } from 'lucide-react';
import { AdminLayout } from '@/components/AdminLayout';
import { api } from '@/lib/api';
import { eur, STATUS_LABELS, STATUS_COLORS, fmtDate } from '@/lib/format';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { detectNewOrders, playOrderChime, showOrderNotification } from '@/lib/notifications';
import { toast } from 'sonner';

const REVENUE_STATUSES = new Set(['accepted', 'preparing', 'ready', 'in_delivery', 'delivered']);

function dayKey(offset = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}

function dayLabel(offset) {
  if (offset === 0) return 'Oggi';
  if (offset === -1) return 'Ieri';
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' });
}

export default function AdminDashboard() {
  const [orders, setOrders] = useState([]);
  const [riders, setRiders] = useState([]);
  const [dayOffset, setDayOffset] = useState(0);  // 0 = today, -1 = yesterday

  useEffect(() => {
    const fetch = () => {
      api.get('/admin/orders').then(r => {
        const list = r.data || [];
        setOrders(list);
        const newOnes = detectNewOrders(list).filter(o => o.status === 'pending');
        if (newOnes.length > 0) {
          playOrderChime();
          newOnes.forEach(o => {
            showOrderNotification(o);
            toast.success(`🛒 Nuovo ordine #${o.order_number} — ${o.customer_name}`, { duration: 8000 });
          });
        }
      }).catch(() => {});
      api.get('/admin/riders').then(r => setRiders(r.data)).catch(() => {});
    };
    fetch();
    const iv = setInterval(fetch, 15000);
    return () => clearInterval(iv);
  }, []);

  const todayKey = dayKey(0);
  const yesterdayKey = dayKey(-1);
  const selectedKey = dayKey(dayOffset);

  // Filter orders to only current & previous day (auto-hide older)
  const recentOrders = useMemo(() => orders.filter(o => {
    const k = (o.created_at || '').slice(0, 10);
    return k === todayKey || k === yesterdayKey;
  }), [orders, todayKey, yesterdayKey]);

  const selectedDayOrders = useMemo(() => orders.filter(o => (o.created_at || '').slice(0, 10) === selectedKey), [orders, selectedKey]);

  const revenueSelected = useMemo(() => selectedDayOrders
    .filter(o => REVENUE_STATUSES.has(o.status))
    .reduce((s, o) => s + Number(o.total || 0), 0), [selectedDayOrders]);
  const deliveredSelected = selectedDayOrders.filter(o => o.status === 'delivered' || o.status === 'ready').length;
  const pendingCount = orders.filter(o => o.status === 'pending' && ((o.created_at || '').slice(0,10) === todayKey || (o.created_at || '').slice(0,10) === yesterdayKey)).length;
  const activeRiders = riders.filter(r => r.active).length;

  return (
    <AdminLayout testid="admin-dashboard">
      <div className="mb-4 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-[--brand-muted]">Panoramica</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold">Dashboard</h1>
        </div>
        <Tabs value={String(dayOffset)} onValueChange={(v) => setDayOffset(parseInt(v))} data-testid="admin-dashboard-day-tabs">
          <TabsList className="bg-[--brand-surface-2] p-1 rounded-xl">
            <TabsTrigger value="0" className="rounded-lg data-[state=active]:bg-white" data-testid="dashboard-tab-today">
              <Calendar size={14} className="mr-1"/>Oggi
            </TabsTrigger>
            <TabsTrigger value="-1" className="rounded-lg data-[state=active]:bg-white" data-testid="dashboard-tab-yesterday">
              Ieri
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="mb-2 text-xs text-[--brand-muted]">Stai visualizzando: <b className="text-[--brand-fg]">{dayLabel(dayOffset)}</b> ({selectedKey})</div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPI icon={ClipboardList} label={`Ordini ${dayLabel(dayOffset).toLowerCase()}`} value={selectedDayOrders.length} testid="admin-kpi-today-orders" />
        <KPI icon={TrendingUp} label={`Fatturato ${dayLabel(dayOffset).toLowerCase()}`} value={eur(revenueSelected)} testid="admin-kpi-revenue" />
        <KPI icon={CheckCircle2} label="Da confermare" value={pendingCount} accent testid="admin-kpi-pending" />
        <KPI icon={Bike} label="Rider attivi" value={activeRiders} testid="admin-kpi-active-riders" />
      </div>

      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl font-bold">Ordini di {dayLabel(dayOffset).toLowerCase()}</h2>
          <Link to="/admin/orders" className="text-sm text-[--brand-primary] font-semibold hover:underline">Vedi tutti →</Link>
        </div>
        <div className="rounded-2xl bg-white border border-[--brand-border] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[--brand-surface-2] text-left">
              <tr>
                <th className="p-3">Numero</th>
                <th className="p-3">Cliente</th>
                <th className="p-3">Totale</th>
                <th className="p-3">Stato</th>
                <th className="p-3 hidden sm:table-cell">Orario</th>
              </tr>
            </thead>
            <tbody>
              {selectedDayOrders.slice(0, 20).map(o => (
                <tr key={o.id} className="border-t border-[--brand-border]" data-testid="admin-dashboard-order-row">
                  <td className="p-3 font-semibold">#{o.order_number}</td>
                  <td className="p-3">{o.customer_name}</td>
                  <td className="p-3 tabular-nums">{eur(o.total)}</td>
                  <td className="p-3"><Badge className={`${STATUS_COLORS[o.status]} border rounded-full`}>{STATUS_LABELS[o.status]}</Badge></td>
                  <td className="p-3 hidden sm:table-cell text-[--brand-muted]">{fmtDate(o.created_at)}</td>
                </tr>
              ))}
              {selectedDayOrders.length === 0 && (
                <tr><td colSpan={5} className="p-6 text-center text-[--brand-muted]">Nessun ordine per {dayLabel(dayOffset).toLowerCase()}.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {orders.length > recentOrders.length && (
        <div className="mt-4 text-xs text-[--brand-muted]">
          💡 La dashboard mostra solo <b>oggi</b> e <b>ieri</b>. Gli ordini più vecchi ({orders.length - recentOrders.length}) sono ancora in <Link to="/admin/orders" className="text-[--brand-primary] underline">Ordini</Link>.
        </div>
      )}
    </AdminLayout>
  );
}

function KPI({ icon: Icon, label, value, accent, testid }) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className={`rounded-2xl border border-[--brand-border] p-5 shadow-[var(--shadow-sm)] ${accent ? 'bg-[--brand-primary] text-white' : 'bg-white'}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-sm opacity-80"><Icon size={16}/> {label}</div>
      <div className="font-display text-3xl font-bold mt-2 tabular-nums">{value}</div>
    </motion.div>
  );
}
